import _orderBy from 'lodash/orderBy';

import type { AloneEventInformation } from 'models';
import {
  eventInformationFields,
  auditingEventInformationFields,
} from 'configs/event-information';

import { Chat, EntityName } from 'features/chat';

import PageTitleView from 'components/page-title-view';
import { EventInformationTable } from 'containers';
import EventDocument from 'containers/event-document';
import {
  Flex,
  PageLoader,
  NoData,
  Error,
  TitledBox,
  FieldsList,
} from 'components';

import clsx from 'clsx';
import { useStyles, List, ListItem } from './style';

type EventInformationDetailsProps = {
  eventInformation: AloneEventInformation | null;
  loading: boolean;
  error: boolean;
  board: boolean;
};

const EventInformationDetails = ({
  eventInformation,
  loading,
  error,
}: EventInformationDetailsProps) => {
  const classes = useStyles();

  if (error) return <Error />;
  if (loading) return <PageLoader />;
  if (!eventInformation) return <NoData />;

  const isAuditingEvent = eventInformation.Type?.Name === 'Tilintarkastus';
  const documentsList = eventInformation.Documents
    ? _orderBy(
        Object.entries(eventInformation.Documents),
        ([key]) => key.toLocaleLowerCase(),
        'asc'
      )
    : null;

  return (
    <>
      <PageTitleView title={isAuditingEvent ? 'Tilintarkastus' : 'Kokoukset'} />
      <Flex>
        <div className={classes.container}>
          <TitledBox
            title={'G_GeneralInformation'}
            cls={{ boxWrapper: classes.boxWrapper }}
          >
            <FieldsList
              data={eventInformation}
              list={
                isAuditingEvent
                  ? auditingEventInformationFields.baseInfo
                  : eventInformationFields.baseInfo
              }
            />
          </TitledBox>
          <TitledBox
            title={'G_PlannedTime'}
            cls={{ boxWrapper: classes.boxWrapper }}
          >
            <FieldsList
              data={eventInformation}
              list={
                isAuditingEvent
                  ? auditingEventInformationFields.planedTime
                  : eventInformationFields.planedTime
              }
            />
          </TitledBox>
          <TitledBox
            title={isAuditingEvent ? 'Tapahtuman kuvaus' : 'G_ActualTime'}
            cls={{ boxWrapper: classes.boxWrapper }}
          >
            <FieldsList
              data={eventInformation}
              list={
                isAuditingEvent
                  ? auditingEventInformationFields.description
                  : eventInformationFields.actualTime
              }
            />
          </TitledBox>
        </div>
        <div className={classes.container}>
          <TitledBox
            title={'Chat'}
            cls={{ box: classes.zeroPadding, boxWrapper: classes.boxWrapper }}
          >
            <Chat
              entityId={eventInformation.Id}
              entityName={EntityName.EventInformation}
            />
          </TitledBox>
          <TitledBox
            title={'Documents'}
            cls={{
              boxWrapper: classes.boxWrapper,
              box: clsx(classes.documentsBox, {
                [classes.centerBox]: !!!documentsList?.length,
              }),
            }}
          >
            {documentsList?.length ? (
              <List>
                {documentsList.map(([key, value]) => (
                  <ListItem key={key}>
                    <EventDocument link={value} name={key} withIcon />
                  </ListItem>
                ))}
              </List>
            ) : (
              <NoData text="FilesAreNotAttached" />
            )}
          </TitledBox>
        </div>
      </Flex>
      <TitledBox
        title={'#portal.massmessage.section.massmessages'}
        cls={{
          box: clsx(classes.zeroPadding, classes.tableBox),
          boxWrapper: clsx(classes.boxWrapper, classes.tableBoxWrapper),
        }}
      >
        <EventInformationTable eventId={eventInformation.Id} />
      </TitledBox>
    </>
  );
};

export default EventInformationDetails;
